'use client';

import { useMemo, type ReactNode } from 'react';
import { TrendDownIcon, TrendUpIcon } from '@phosphor-icons/react';

import { EChart, type EChartsOption } from '@/components/charts/EChart';
import { buildTooltip } from '@/components/charts/EChartTooltip';
import { useNetWorthTrend } from '@/hooks/queries/useNetWorthTrend';
import { useCompactCurrency } from '@/hooks/useCompactCurrency';
import { token } from '@/lib/theme/tokens';
import { useThemeColors } from '@/lib/theme/useThemeColors';
import { cn } from '@/lib/utils';
import type { NetWorthTrendWidget } from '@/types';

import { WidgetCard, widgetState } from './WidgetCard';

/**
 * W4 · Investimentos · 6 meses (renamed from Patrimônio) — invested-wealth headline plus a 6-month bar trend.
 * Investments only by product decision: the checking balance is working capital
 * (Disponível's story) and card debt already lives in Em aberto. One component
 * serves both surfaces: desktop shows the subline; the mobile carousel slide
 * swaps it for a MoM delta chip and a shorter chart.
 */
export function PatrimonioWidget(): ReactNode {
  const { data, error, isLoading } = useNetWorthTrend();

  return (
    <WidgetCard
      eyebrow="Investimentos · 6 meses"
      action={{ label: 'Investir', href: '/investments' }}
      state={widgetState(data, error, isLoading, isTrendEmpty)}
      emptyLabel="Sem investimentos ainda"
    >
      <NetWorthBody data={data} />
    </WidgetCard>
  );
}

function isTrendEmpty(data: NetWorthTrendWidget): boolean {
  return data.months.length === 0;
}

function NetWorthBody({ data }: { data: NetWorthTrendWidget | undefined }): ReactNode {
  // The prototype's two compact spellings: 'mil' → "R$ 90,9 mil" (headline/
  // tooltip), 'k' → "R$ 7,2k" (delta chip) — always one decimal, privacy-aware.
  const oneDecimal = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
  const compactMil = useCompactCurrency({ style: 'mil', ...oneDecimal });
  const compactK = useCompactCurrency({ style: 'k', ...oneDecimal });
  const palette = useThemeColors();

  const option = useMemo<EChartsOption>(() => {
    if (!data || data.months.length === 0) return {};
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: buildTooltip({ format: (v) => compactMil(v) }),
      },
      grid: { left: 4, right: 4, top: 8, bottom: 0, containLabel: true },
      xAxis: {
        type: 'category',
        data: data.months.map((m) => m.label),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          fontSize: 10,
          fontFamily: token('--font-mono', "'DM Mono', ui-monospace, monospace"),
          color: palette.textTertiary,
          margin: 8,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: { show: false },
        splitLine: { lineStyle: { type: 'dashed', color: palette.border } },
      },
      series: [
        {
          name: 'Investido',
          type: 'bar',
          data: data.months.map((m) => m.total),
          itemStyle: { color: palette.amountPositive, borderRadius: [3, 3, 0, 0] },
          barCategoryGap: '38%',
        },
      ],
    };
  }, [data, palette, compactMil]);

  if (!data || data.months.length === 0) return null;

  const deltaPositive = data.momDelta >= 0;
  const DeltaIcon = deltaPositive ? TrendUpIcon : TrendDownIcon;
  const deltaLabel = `${deltaPositive ? '+' : ''}${compactK(data.momDelta)}`;

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-3xl font-semibold tracking-tight text-text-primary tabular-nums">
          {compactMil(data.total)}
        </span>
        {/* MoM delta chip — the mobile carousel slide's companion to the headline. */}
        <span
          data-testid="net-worth-delta"
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[11px] font-semibold tabular-nums sm:hidden',
            deltaPositive ? 'bg-success-surface text-success-text' : 'bg-error-surface text-error-text',
          )}
        >
          <DeltaIcon size={11} weight="fill" aria-hidden />
          {deltaLabel}
        </span>
      </div>
      <p className="mt-0.5 hidden text-xs text-text-tertiary sm:block">patrimônio investido</p>

      <div className="mt-3 h-[110px] flex-1 sm:mt-4 sm:h-[150px]">
        <EChart option={option} />
      </div>
    </div>
  );
}
